// src/Components/Pages/TodoList/PatchCallFormModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { handleAddModifyOperation } from '../../../Api/ApiMethods';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

const PatchCallFormWorkInfoModal = ({ isOpen, toggle, callFormId, initialData, refreshData }) => {
    const initialFormData = {
        documentType: 'work', // 'work' or 'trial'
        bonusEligibility: null, // API'deki alan adıyla eşleşmesi için değiştirildi
    };

    const [formData, setFormData] = useState(initialFormData);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setFormData({
                    documentType: initialData.isTrialDocument === true ? 'trial' : 'work',
                    bonusEligibility: initialData.bonusEligibility !== undefined ? initialData.bonusEligibility : null,
                });
            } else {
                // Default values if no initialData is provided (though it should be)
                setFormData(initialFormData);
            }
        }
    }, [isOpen, initialData]);

    const handleRadioChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "bonusEligibility") { // Alan adı 'bonusEligibility' olarak güncellendi
            let processedValue = value;
            processedValue = processedValue.replace(/[^0-9.]/g, '');
            const decimalCheck = processedValue.split('.');
            if (decimalCheck.length > 2) {
                processedValue = decimalCheck[0] + '.' + decimalCheck.slice(1).join('');
            }
            if (decimalCheck[1] && decimalCheck[1].length > 2) {
                processedValue = decimalCheck[0] + '.' + decimalCheck[1].slice(0, 2);
            }
            setFormData(prev => ({ ...prev, [name]: processedValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async () => {
        if (!callFormId) {
            toast.error("Güncellenecek çağrı formu ID'si bulunamadı.");
            return;
        }
        setIsSaving(true);

        const amount = formData.bonusEligibility === '' || formData.bonusEligibility === null
            ? null
            : parseFloat(formData.bonusEligibility);

        if (formData.bonusEligibility !== '' && formData.bonusEligibility !== null && isNaN(amount)) {
            toast.error("Prim hakediş tutarı geçerli bir sayı olmalıdır.");
            setIsSaving(false);
            return;
        }

        const payload = {
            isTrialDocument: formData.documentType === 'trial',
            isWorkDocument: formData.documentType === 'work',
            bonusEligibility: amount, // Alan adı 'bonusEligibility' olarak güncellendi
        };

        // Ensure one document type is true
        if (payload.isTrialDocument) {
            payload.isWorkDocument = false;
        } else {
            payload.isWorkDocument = true; // Default to work if trial is not explicitly selected
        }
        
        const apiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/callform/patch-callform`;

        try {
            await handleAddModifyOperation({
                apiEndpoint: apiEndpoint,
                selectedItem: { id: callFormId }, // This ID will be appended to the apiEndpoint by handleAddModifyOperation
                mode: 'modify', // Triggers a PUT request
                data: payload,
                refreshDataCallback: () => {
                    if (refreshData) refreshData();
                    toast.success('Dosya bilgileri başarıyla güncellendi.');
                },
                componentName: 'Dosya Bilgisi',
            });
            toggle(); // Close modal on success
        } catch (error) {
            // Error is usually handled within handleAddModifyOperation, but catch here for safety
            console.error("Dosya bilgisi güncelleme hatası:", error);
            // toast.error("Dosya bilgileri güncellenirken bir hata oluştu."); // Already handled by handleAddModifyOperation
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>Dosya Bilgilerini Güncelle</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup tag="fieldset" className="mb-3">
                        <Label>Dosya Tipi</Label>
                        <div className="d-flex mt-2">
                            <FormGroup check className="radio radio-primary me-3">
                                <Input
                                    id="documentTypeWork"
                                    name="documentType"
                                    type="radio"
                                    value="work"
                                    checked={formData.documentType === 'work'}
                                    onChange={handleRadioChange}
                                />
                                <Label check for="documentTypeWork" className="ms-1 mb-0">İş Dosyası</Label>
                            </FormGroup>
                            <FormGroup check className="radio radio-primary">
                                <Input
                                    id="documentTypeTrial"
                                    name="documentType"
                                    type="radio"
                                    value="trial"
                                    checked={formData.documentType === 'trial'}
                                    onChange={handleRadioChange}
                                />
                                <Label check for="documentTypeTrial" className="ms-1 mb-0">Deneme Dosyası</Label>
                            </FormGroup>
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <Label for="bonusEligibility">Prim Hakediş Tutarı:</Label>
                        <Input
                            type="text"
                            id="bonusEligibility"
                            name="bonusEligibility"
                            value={formData.bonusEligibility === null ? '' : formData.bonusEligibility}
                            onChange={handleInputChange}
                            placeholder="Örn: 150.75 (Opsiyonel)"
                        />
                        {/* Add FormFeedback here if validation errors are needed */}
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
                <Button color="secondary" onClick={handleCancel} className="ms-2" disabled={isSaving}>
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

PatchCallFormWorkInfoModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    callFormId: PropTypes.string,
    initialData: PropTypes.shape({
        isTrialDocument: PropTypes.bool,
        bonusEligibility: PropTypes.number, // API'deki alan adıyla eşleşmesi için güncellendi
    }),
    refreshData: PropTypes.func,
};

export default PatchCallFormWorkInfoModal;